import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import Divider from '~/bootcamp/Activities/components/Divider'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { AppLanguage } from '~/types/locale'

const FRACTION_VALUES = [0.5, 0.25, 0.2, 0.1]

function UnitFractionFraction({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC: SubmitButton,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const language = useRouter().locale as AppLanguage

  const [answer, setAnswer] = useState({ denominator: '', numerator: '' })
  const [fractionValueIndex, setFractionValueIndex] = useState<number>(0)
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer({ denominator: '', numerator: '' })
    setFractionValueIndex(randomIntFromInterval(0, FRACTION_VALUES.length - 1, taskHistory))
  }, [taskHistory])

  // `answer` is the student's own typing and never rides the emit.
  const paintedValue =
    language === 'no'
      ? FRACTION_VALUES[fractionValueIndex].toString().replace('.', ',')
      : FRACTION_VALUES[fractionValueIndex].toString()
  useEmitInstance(onInstanceChange, `${paintedValue} = \\frac{\\square}{\\square}`)

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect =
      answer.numerator === '1' &&
      (Number(answer.numerator) / Number(answer.denominator)).toFixed(2) ===
        FRACTION_VALUES[fractionValueIndex]?.toFixed(2)
    onAnswerStateChange(isCorrect)
  }

  const resetAnswer = (): void => {
    setAnswer({
      denominator: '',
      numerator: '',
    })
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [fractionValueIndex, ...prevState].slice(0, 3)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer.denominator !== '' && answer.numerator !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        {language === 'no'
          ? FRACTION_VALUES[fractionValueIndex].toString().replace('.', ',')
          : FRACTION_VALUES[fractionValueIndex]}
        <Equals />
        <div className="ml-4 flex flex-col items-center">
          <Input
            aria-label={t('ariaLabelButtons.insertNumerator')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={`${inputKey}-first`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: state.denominator, numerator: value }))}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer.numerator}
          />
          <Divider className="my-2" />
          <Input
            aria-label={t('ariaLabelButtons.insertDenominator')}
            isError={answerState === false}
            key={`${inputKey}-second`}
            maxDigits={4}
            onChange={(value) => setAnswer((state) => ({ denominator: value, numerator: state.numerator }))}
            onEnter={onEnterPress}
            value={answer.denominator}
          />
        </div>
      </FractionsActivityWrapper>
      {SubmitButton && (
        <SubmitButton
          clearFormAction={() => resetAnswer()}
          correctAnswer={answerState ?? false}
          formValid={answer.denominator !== '' && answer.numerator !== ''}
          goToNextQuestionAction={handleOnNextQuestionClick}
          submitAnswerAction={handleOnCheckAnswerClick}
        />
      )}
    </ActivityWrapper>
  )
}

export default UnitFractionFraction
